import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState, Skeleton } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { money, relative, statusFamily, toBase } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { CurrencyInput } from "@/components/common/CurrencyInput";
import { Plus, Target, LayoutGrid, Table2, Search, Repeat } from "lucide-react";
import { toast } from "sonner";

const SOURCES = ["referral", "inbound", "outbound", "event", "partner", "other"];
const BORDER = { won: "border-t-emerald-500", lost: "border-t-red-500", standard: "border-t-blue-400" };

function useUsersMap() {
  const { data = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const map = useMemo(() => Object.fromEntries(data.map((u) => [u.id, u])), [data]);
  return { users: data, map };
}

export default function LeadsBoard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can, me } = useApp();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState(() => localStorage.getItem("crm-view") || "board");
  const [search, setSearch] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [newOpen, setNewOpen] = useState(params.get("new") === "1");
  const [dragId, setDragId] = useState(null);
  const [lostModal, setLostModal] = useState(null); // { leadId, stageId }
  const [newTypeOpen, setNewTypeOpen] = useState(false);
  const [typeId, setTypeId] = useState(() => localStorage.getItem("crm-project-type") || "all");
  const [pipelineId, setPipelineId] = useState(() => localStorage.getItem("crm-pipeline") || null);
  const { users, map } = useUsersMap();

  const { data: pipelines = [] } = useQuery({ queryKey: ["pipelines"], queryFn: () => get("/crm/pipelines") });
  const { data: projectTypes = [] } = useQuery({ queryKey: ["project-types"], queryFn: () => get("/crm/project-types") });
  const { data: rates } = useQuery({ queryKey: ["exchange-rates"], queryFn: () => get("/crm/exchange-rates") });
  const { data: leads, isLoading } = useQuery({ queryKey: ["leads"], queryFn: () => get("/crm/leads") });

  const setType = (id) => { setTypeId(id); localStorage.setItem("crm-project-type", id); setPipelineId(null); localStorage.removeItem("crm-pipeline"); };
  const setPipeline = (id) => { setPipelineId(id); localStorage.setItem("crm-pipeline", id); };

  const pipelinesForType = useMemo(() => {
    if (typeId === "all") return pipelines;
    return pipelines.filter((p) => p.project_type_id === typeId);
  }, [pipelines, typeId]);

  const pipeline = useMemo(() => {
    if (pipelineId) {
      const found = pipelinesForType.find((p) => p.id === pipelineId);
      if (found) return found;
    }
    return pipelinesForType.length === 1 ? pipelinesForType[0] : null;
  }, [pipelineId, pipelinesForType]);

  const move = useMutation({
    mutationFn: ({ id, body }) => patch(`/crm/leads/${id}`, body),
    onMutate: async ({ id, body }) => {
      await qc.cancelQueries({ queryKey: ["leads"] });
      const prev = qc.getQueryData(["leads"]);
      qc.setQueryData(["leads"], (old) => old.map((l) => (l.id === id ? { ...l, ...body } : l)));
      return { prev };
    },
    onError: (e, v, ctx) => qc.setQueryData(["leads"], ctx.prev),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leads"] }); toast.success("Lead updated"); },
  });

  const setViewPref = (v) => { setView(v); localStorage.setItem("crm-view", v); };

  const filtered = useMemo(() => {
    if (!leads || !pipeline) return [];
    return leads.filter((l) => {
      if (l.pipeline_id !== pipeline.id) return false;
      if (search && !`${l.company_name} ${l.contact_name}`.toLowerCase().includes(search.toLowerCase())) return false;
      if (ownerFilter !== "all" && l.owner_id !== ownerFilter) return false;
      return true;
    });
  }, [leads, pipeline, search, ownerFilter]);

  const columnSum = (cards) => {
    const total = cards.reduce((sum, l) => sum + (toBase(l.deal_value, l.currency, rates) || 0), 0);
    return money(total, rates?.base || "USD");
  };

  const stageType = (id) => pipeline?.stages.find((s) => s.id === id)?.type;

  const onDrop = (stage) => {
    if (!dragId) return;
    const id = dragId; setDragId(null);
    if (stage.type === "lost") { setLostModal({ leadId: id, stageId: stage.id }); return; }
    move.mutate({ id, body: { stage_id: stage.id } });
  };

  return (
    <div>
      <PageHeader title="Leads" subtitle="Track your pre-sales pipeline from first contact to close.">
        <div className="flex rounded-lg border border-border bg-card p-0.5">
          <button data-testid="view-board" onClick={() => setViewPref("board")}
            className={cn("flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm", view === "board" ? "bg-primary text-primary-foreground" : "text-muted-foreground")}>
            <LayoutGrid className="h-4 w-4" />Board
          </button>
          <button data-testid="view-table" onClick={() => setViewPref("table")}
            className={cn("flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm", view === "table" ? "bg-primary text-primary-foreground" : "text-muted-foreground")}>
            <Table2 className="h-4 w-4" />Table
          </button>
        </div>
        {can("crm", "create") && (
          <Button data-testid="new-lead-btn" disabled={!pipeline} onClick={() => setNewOpen(true)}><Plus className="mr-1.5 h-4 w-4" />New Lead</Button>
        )}
      </PageHeader>

      <div className="mb-6 space-y-2">
        <div className="flex flex-wrap items-center gap-2" data-testid="project-type-chips">
          <button data-testid="type-chip-all" onClick={() => setType("all")}
            className={cn("rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              typeId === "all" ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground")}>
            All Types
          </button>
          {projectTypes.filter((t) => t.active).map((t) => (
            <button key={t.id} data-testid={`type-chip-${t.id}`} onClick={() => setType(t.id)}
              className={cn("rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                typeId === t.id ? "text-white" : "border-border bg-card text-muted-foreground hover:text-foreground")}
              style={typeId === t.id ? { backgroundColor: t.color, borderColor: t.color } : {}}>
              {t.name}
            </button>
          ))}
          <button data-testid="type-chip-new" title="New project type" onClick={() => setNewTypeOpen(true)}
            className="rounded-full border border-dashed border-border px-2.5 py-1 text-xs text-muted-foreground hover:border-primary hover:text-primary">
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>

        {pipelinesForType.length > 1 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="pipeline-pills">
            {pipelinesForType.map((p) => (
              <button key={p.id} data-testid={`pipeline-pill-${p.id}`} onClick={() => setPipeline(p.id)}
                className={cn("flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                  pipeline?.id === p.id ? "text-white" : "border-border bg-card text-muted-foreground hover:text-foreground")}
                style={pipeline?.id === p.id ? { backgroundColor: p.color, borderColor: p.color } : {}}>
                {p.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mb-4 flex items-center gap-3">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="lead-search" placeholder="Search company or contact" value={search}
            onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={ownerFilter} onValueChange={setOwnerFilter}>
          <SelectTrigger className="w-44" data-testid="owner-filter"><SelectValue placeholder="All owners" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All owners</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-6 gap-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-96" />)}</div>
      ) : !pipeline ? (
        <EmptyState icon={Target} title="Choose a pipeline" subtitle="Select a pipeline above to view its board." />
      ) : view === "board" ? (
        <div className="flex gap-3 overflow-x-auto rounded-xl pb-4" data-testid="leads-board"
          style={{ backgroundColor: `${pipeline.color}0A` }}>
          {[...pipeline.stages].sort((a, b) => a.order - b.order).map((stage) => {
            const cards = filtered.filter((l) => l.stage_id === stage.id);
            return (
              <div key={stage.id} data-testid={`board-col-${stage.id}`}
                onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(stage)}
                className="flex w-72 shrink-0 flex-col rounded-xl bg-muted/40">
                <div className={cn("rounded-t-xl border-t-[3px] bg-card px-3 py-2.5", BORDER[stage.type])}
                  style={{ borderTopColor: stage.color || undefined }}>
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] font-semibold text-foreground">{stage.name}</span>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{cards.length}</span>
                  </div>
                  {cards.length > 0 && (
                    <div className="mt-0.5 text-right text-[11px] text-muted-foreground">{columnSum(cards)}</div>
                  )}
                </div>
                <div className="flex flex-col gap-2 p-2">
                  {cards.map((l) => (
                    <div key={l.id} data-testid={`lead-card-${l.id}`} draggable
                      onDragStart={() => setDragId(l.id)} onDragEnd={() => setDragId(null)}
                      onClick={() => navigate(`/crm/leads/${l.id}`)}
                      className={cn("cursor-pointer rounded-lg border border-border bg-card p-3 transition-all hover:border-primary/40 hover:shadow-sm",
                        dragId === l.id && "opacity-50")}>
                      <div className="flex items-start justify-between gap-2">
                        <span className="flex items-center gap-1 text-[13px] font-medium leading-tight text-foreground">
                          {l.company_name}
                          {l.existing_client_id && <Repeat className="h-3 w-3 text-violet-500" />}
                        </span>
                        <span className="shrink-0 text-[13px] font-semibold text-foreground">{money(l.deal_value, l.currency)}</span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">{l.contact_name}</div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {l.tags?.slice(0, 3).map((t) => (
                          <span key={t} className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{t}</span>
                        ))}
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <Avatar user={map[l.owner_id]} size="xs" />
                        <span className="text-[10px] capitalize text-muted-foreground">{l.source}</span>
                      </div>
                    </div>
                  ))}
                  {cards.length === 0 && <div className="py-6 text-center text-xs text-muted-foreground">No leads</div>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <LeadTable leads={filtered} pipeline={pipeline} map={map} users={users} navigate={navigate} move={move} can={can} />
      )}

      {filtered.length === 0 && !isLoading && view === "table" && (
        <EmptyState icon={Target} title="No leads found" subtitle="Adjust your filters or create a new lead." />
      )}

      <NewLeadModal open={newOpen} setOpen={(v) => { setNewOpen(v); if (!v) setParams({}); }}
        pipeline={pipeline} pipelines={pipelines} projectTypes={projectTypes} users={users} me={me} qc={qc} navigate={navigate} />
      <QuickProjectTypeModal open={newTypeOpen} setOpen={setNewTypeOpen} qc={qc} onCreated={setType} />
      <LostReasonModal state={lostModal} setState={setLostModal} onConfirm={(reason) => {
        move.mutate({ id: lostModal.leadId, body: { stage_id: lostModal.stageId, lost_reason: reason } });
        setLostModal(null);
      }} />
    </div>
  );
}

function LeadTable({ leads, pipeline, map, users, navigate, move, can }) {
  const stageName = (id) => pipeline?.stages.find((s) => s.id === id)?.name;
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <table className="w-full text-sm" data-testid="leads-table">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-4 py-3 font-semibold">Company</th>
            <th className="px-4 py-3 font-semibold">Contact</th>
            <th className="px-4 py-3 font-semibold">Stage</th>
            <th className="px-4 py-3 text-right font-semibold">Value</th>
            <th className="px-4 py-3 font-semibold">Owner</th>
            <th className="px-4 py-3 font-semibold">Source</th>
            <th className="px-4 py-3 font-semibold">Last Activity</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.id} data-testid={`lead-tr-${l.id}`} className="border-b border-border last:border-0 hover:bg-muted/50">
              <td className="cursor-pointer px-4 py-3 font-medium text-foreground" onClick={() => navigate(`/crm/leads/${l.id}`)}>
                <span className="flex items-center gap-1">{l.company_name}
                  {l.existing_client_id && <Repeat className="h-3 w-3 text-violet-500" />}</span>
              </td>
              <td className="px-4 py-3 text-muted-foreground">{l.contact_name}</td>
              <td className="px-4 py-3">
                {can("crm", "edit") ? (
                  <Select value={l.stage_id} onValueChange={(v) => move.mutate({ id: l.id, body: { stage_id: v } })}>
                    <SelectTrigger className="h-7 w-36" data-testid={`inline-stage-${l.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{pipeline?.stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <StatusBadge status={stageName(l.stage_id)} />}
              </td>
              <td className="px-4 py-3 text-right font-medium">{money(l.deal_value, l.currency)}</td>
              <td className="px-4 py-3"><div className="flex items-center gap-1.5"><Avatar user={map[l.owner_id]} size="xs" /><span className="text-xs">{map[l.owner_id]?.name}</span></div></td>
              <td className="px-4 py-3 capitalize text-muted-foreground">{l.source}</td>
              <td className="px-4 py-3 text-muted-foreground">{relative(l.last_activity_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const FORECAST_CATEGORIES = ["Committed", "Best Case", "Pipeline"];

function NewLeadModal({ open, setOpen, pipeline, pipelines, projectTypes, users, me, qc, navigate }) {
  const DEFAULTS = { source: "inbound", probability: 50, currency: "USD", deal_type: "new_prospect", competitor_ids: [] };
  const [form, setForm] = useState(DEFAULTS);
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: () => get("/crm/clients"), enabled: open });
  const { data: competitors = [] } = useQuery({ queryKey: ["competitors"], queryFn: () => get("/crm/competitors"), enabled: open });
  const qc2 = useQueryClient();

  useEffect(() => {
    if (open) {
      setForm({ ...DEFAULTS, owner_id: me?.id, project_type_id: pipeline?.project_type_id || null, pipeline_id: pipeline?.id || null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const create = useMutation({
    mutationFn: (body) => post("/crm/leads", body),
    onSuccess: (lead) => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      toast.success("Lead created");
      setOpen(false);
      navigate(`/crm/leads/${lead.id}`);
    },
  });
  const addCompetitor = useMutation({
    mutationFn: (name) => post("/crm/competitors", { name }),
    onSuccess: (c) => {
      qc2.invalidateQueries({ queryKey: ["competitors"] });
      set("competitor_ids", [...(form.competitor_ids || []), c.id]);
    },
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pipelinesForType = form.project_type_id
    ? pipelines.filter((p) => p.project_type_id === form.project_type_id)
    : pipelines;
  const selectedPipeline = pipelines.find((p) => p.id === form.pipeline_id);
  const isExisting = form.deal_type === "existing_client";

  const addTag = (e) => {
    if (e.key !== "Enter" || !e.target.value.trim()) return;
    e.preventDefault();
    set("tags", [...(form.tags || []), e.target.value.trim()]);
    e.target.value = "";
  };

  const submit = () => {
    if (!selectedPipeline) { toast.error("Select a pipeline"); return; }
    if (isExisting) {
      if (!form.existing_client_id) { toast.error("Select a client"); return; }
    } else if (!form.company_name || !form.contact_name) {
      toast.error("Company and contact are required"); return;
    }
    const firstStage = [...selectedPipeline.stages].sort((a, b) => a.order - b.order)[0];
    const client = clients.find((c) => c.id === form.existing_client_id);
    create.mutate({
      ...form,
      company_name: isExisting ? client?.name : form.company_name,
      pipeline_id: selectedPipeline.id, stage_id: firstStage?.id,
      owner_id: form.owner_id || users[0]?.id,
      tags: form.tags || [],
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="new-lead-modal">
        <DialogHeader><DialogTitle>New Lead</DialogTitle></DialogHeader>

        <div className="flex rounded-lg border border-border bg-muted/40 p-0.5">
          {[{ v: "new_prospect", l: "New Prospect" }, { v: "existing_client", l: "Existing Client" }].map((o) => (
            <button key={o.v} data-testid={`deal-type-${o.v}`} onClick={() => set("deal_type", o.v)}
              className={cn("flex-1 rounded-md px-3 py-1.5 text-sm", form.deal_type === o.v ? "bg-primary text-primary-foreground" : "text-muted-foreground")}>
              {o.l}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Project Type</Label>
            <Select value={form.project_type_id || "none"} onValueChange={(v) => set("project_type_id", v === "none" ? null : v)}>
              <SelectTrigger className="mt-1" data-testid="nl-project-type"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Any</SelectItem>
                {projectTypes.filter((t) => t.active).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Pipeline *</Label>
            <Select value={form.pipeline_id || undefined} disabled={pipelinesForType.length === 1}
              onValueChange={(v) => set("pipeline_id", v)}>
              <SelectTrigger className="mt-1" data-testid="nl-pipeline"><SelectValue placeholder="Select pipeline" /></SelectTrigger>
              <SelectContent>{pipelinesForType.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {isExisting ? (
            <div className="col-span-2">
              <Label>Client *</Label>
              <Select value={form.existing_client_id} onValueChange={(v) => set("existing_client_id", v)}>
                <SelectTrigger className="mt-1" data-testid="nl-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ) : (
            <>
              <div><Label>Company Name *</Label><Input data-testid="nl-company" className="mt-1" value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} /></div>
            </>
          )}
          <div><Label>Contact Name {!isExisting && "*"}</Label><Input data-testid="nl-contact" className="mt-1" value={form.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} /></div>
          <div><Label>Contact Email</Label><Input className="mt-1" value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} /></div>
          <div><Label>Contact Phone</Label><Input className="mt-1" value={form.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} /></div>

          <div>
            <Label>Deal Value</Label>
            <div className="mt-1">
              <CurrencyInput testid="nl-value" value={form.deal_value ?? ""} currency={form.currency}
                onChangeValue={(v) => set("deal_value", v)} onChangeCurrency={(v) => set("currency", v)} />
            </div>
          </div>
          <div><Label>Expected Close Date</Label><Input type="date" className="mt-1" value={form.expected_close_date || ""} onChange={(e) => set("expected_close_date", e.target.value)} /></div>

          <div className="col-span-2">
            <Label>Probability — {form.probability}%</Label>
            <Slider className="mt-3" value={[form.probability]} max={100} step={5} onValueChange={([v]) => set("probability", v)} />
          </div>

          <div><Label>Lead Source *</Label>
            <Select value={form.source} onValueChange={(v) => set("source", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Owner *</Label>
            <Select value={form.owner_id} onValueChange={(v) => set("owner_id", v)}>
              <SelectTrigger className="mt-1" data-testid="nl-owner"><SelectValue placeholder="Assign owner" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label>Forecast Category</Label>
            <Select value={form.forecast_category || "none"} onValueChange={(v) => set("forecast_category", v === "none" ? null : v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {FORECAST_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Competitors Known</Label>
            <CompetitorMultiSelect competitors={competitors} selected={form.competitor_ids || []}
              onChange={(ids) => set("competitor_ids", ids)}
              onAddNew={(name) => addCompetitor.mutate(name)} />
          </div>

          <div className="col-span-2">
            <Label>Tags</Label>
            <div className="mt-1 flex flex-wrap gap-1 rounded-md border border-input px-2 py-1.5">
              {(form.tags || []).map((t) => (
                <span key={t} className="flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs">
                  {t}
                  <button onClick={() => set("tags", form.tags.filter((x) => x !== t))} className="text-muted-foreground hover:text-foreground">×</button>
                </span>
              ))}
              <input data-testid="nl-tags" className="min-w-[80px] flex-1 bg-transparent text-sm outline-none" placeholder="Type and press Enter" onKeyDown={addTag} />
            </div>
          </div>

          <div className="col-span-2"><Label>Description</Label><Textarea className="mt-1" value={form.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="nl-submit" onClick={submit} disabled={create.isPending}>Create Lead</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompetitorMultiSelect({ competitors, selected, onChange, onAddNew }) {
  const [popOpen, setPopOpen] = useState(false);
  const [query, setQuery] = useState("");
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const exactMatch = competitors.some((c) => c.name.toLowerCase() === query.trim().toLowerCase());

  return (
    <Popover open={popOpen} onOpenChange={setPopOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="nl-competitors" className="mt-1 flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border border-input px-2 py-1.5 text-left text-sm">
          {selected.length === 0 ? <span className="text-muted-foreground">Select competitors</span> :
            competitors.filter((c) => selected.includes(c.id)).map((c) => (
              <span key={c.id} className="rounded bg-muted px-1.5 py-0.5 text-xs">{c.name}</span>
            ))}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0">
        <Command>
          <CommandInput placeholder="Search competitors..." value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>
              {query.trim() && !exactMatch && (
                <button className="flex w-full items-center gap-1 px-3 py-2 text-left text-sm text-primary hover:bg-muted"
                  onClick={() => { onAddNew(query.trim()); setQuery(""); }}>
                  <Plus className="h-3.5 w-3.5" />Add new competitor "{query.trim()}"
                </button>
              )}
            </CommandEmpty>
            <CommandGroup>
              {competitors.map((c) => (
                <CommandItem key={c.id} value={c.name} onSelect={() => toggle(c.id)}>
                  <input type="checkbox" readOnly checked={selected.includes(c.id)} className="mr-2" />
                  {c.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function QuickProjectTypeModal({ open, setOpen, qc, onCreated }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("#6366F1");
  const create = useMutation({
    mutationFn: (body) => post("/crm/project-types", body),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ["project-types"] });
      toast.success("Project type created");
      setOpen(false); setName(""); setColor("#6366F1");
      onCreated(t.id);
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm" data-testid="quick-project-type-modal">
        <DialogHeader><DialogTitle>New Project Type</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input data-testid="qpt-name" className="mt-1" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div>
            <Label>Color</Label>
            <div className="mt-1 flex items-center gap-2">
              <input type="color" data-testid="qpt-color" className="h-9 w-12 cursor-pointer rounded border border-border bg-transparent"
                value={color} onChange={(e) => setColor(e.target.value)} />
              <Input value={color} onChange={(e) => setColor(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="qpt-submit" onClick={() => { if (!name) { toast.error("Name is required"); return; } create.mutate({ name, color }); }}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LostReasonModal({ state, setState, onConfirm }) {
  const [reason, setReason] = useState("Competitor");
  return (
    <Dialog open={!!state} onOpenChange={(v) => !v && setState(null)}>
      <DialogContent className="max-w-sm" data-testid="lost-reason-modal">
        <DialogHeader><DialogTitle>Mark as Lost</DialogTitle></DialogHeader>
        <Label>Lost Reason *</Label>
        <Select value={reason} onValueChange={setReason}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>{["Budget", "Timeline", "Competitor", "No Response", "Other"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setState(null)}>Cancel</Button>
          <Button variant="destructive" data-testid="confirm-lost" onClick={() => onConfirm(reason)}>Confirm Lost</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
